/**
 * Credencial fija para acciones destructivas sobre un evento (borrar todas
 * sus órdenes). La pide la pantalla de configuración del evento además del
 * rol: nombre y contraseña tienen que coincidir exactamente.
 *
 * La contraseña NO se guarda en claro: solo su SHA-256, así no queda
 * legible en el bundle. Para cambiarla, generar el nuevo hash con
 * `printf '%s' 'nueva' | shasum -a 256` y reemplazar EVENT_ADMIN_PASSWORD_SHA256.
 */
export const EVENT_ADMIN_NAME = 'Fer D. Melgar';
export const EVENT_ADMIN_PASSWORD_SHA256 = '05a2cb2513ba62b8dab06606a2e0054ca99bd54eb2ae5569756d6a86d8584984';

/** Nombre exacto (sin espacios sobrantes) y contraseña con el hash registrado. */
export function verifyEventAdminCredentials(name: string, password: string): boolean {
    const nameOk = String(name || '').trim() === EVENT_ADMIN_NAME;
    const passwordOk = sha256Hex(String(password || '')) === EVENT_ADMIN_PASSWORD_SHA256;
    return nameOk && passwordOk;
}

// ─── SHA-256 (implementación propia: crypto.subtle no está en contextos
//     no seguros como file:// de Cordova y además es asíncrona) ──────────

const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function utf8Bytes(text: string): number[] {
    const out: number[] = [];
    const encoded = encodeURIComponent(text);
    for (let i = 0; i < encoded.length; i++) {
        const ch = encoded.charAt(i);
        if (ch === '%') {
            out.push(parseInt(encoded.substr(i + 1, 2), 16));
            i += 2;
        } else {
            out.push(ch.charCodeAt(0));
        }
    }
    return out;
}

export function sha256Hex(text: string): string {
    const bytes = utf8Bytes(text);
    const bitLength = bytes.length * 8;

    // Relleno: 0x80, ceros hasta 56 mod 64 y la longitud en 64 bits big-endian.
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (let i = 7; i >= 0; i--) bytes.push(i >= 4 ? 0 : (bitLength >>> (i * 8)) & 0xff);

    let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
    let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;
    const w = new Array<number>(64);
    const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

    for (let offset = 0; offset < bytes.length; offset += 64) {
        for (let i = 0; i < 16; i++) {
            const j = offset + i * 4;
            w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0;
        }
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }

        let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const temp1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
            const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (S0 + maj) >>> 0;
            h = g; g = f; f = e;
            e = (d + temp1) >>> 0;
            d = c; c = b; b = a;
            a = (temp1 + temp2) >>> 0;
        }
        h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
        h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
    }

    return [h0, h1, h2, h3, h4, h5, h6, h7]
        .map(v => ('00000000' + v.toString(16)).slice(-8))
        .join('');
}
